import {
	Flocks,
	getHighlightLevel,
	HighlightLevel,
	highlightLevelToHighlightColor,
} from "./Flocks";
import { FormattedMessage } from "react-intl";
import { FarmType, IFarm } from "../../interfaces/Farm";
import { useFarms } from "../../hooks/useFarms";
import { useQueryParameter } from "../../hooks/useQueryParameter";
import "./Farms.scss";
import { DynamicRoutes, StaticRoutes } from "../../routes";
import { Link } from "../../components/common/Link";
import { GlobalErrorPage } from "../../components/common/GlobalErrorPage";
import { PageLoader } from "../../components/common/Loader";
import { useAuthLoggedIn } from "../../context/AuthProvider";
import { isCustomerManagementRole, UserRole } from "../../interfaces/UserRole";
import { FarmSamples } from "./FarmSamples";
import { Button } from "../../components/common/Button";
import Icon from "@mdi/react";
import { mdiPlus, mdiInformation } from "@mdi/js";

const farmsViewTabURLParameter = "tab";

export function Farms() {
	const {
		user: { role },
	} = useAuthLoggedIn();
	const canCreateNewFarms = isCustomerManagementRole(role as UserRole);
	const { farms, loading } = useFarms();
	const selectedTab = parseInt(
		useQueryParameter<string>(farmsViewTabURLParameter) || "0"
	);

	if (loading) {
		return <PageLoader />;
	}

	if (!farms) {
		return (
			<GlobalErrorPage>
				<FormattedMessage id="Farm_Error_NotFound" />
			</GlobalErrorPage>
		);
	}

	return (
		<div className="Farms">
			{farms.length ? (
				<>
					<Tabs farms={farms} selectedTab={selectedTab} />
					<Farm farm={farms[selectedTab]} />
				</>
			) : (
				<NoAccessRights canCreateNewFarms={canCreateNewFarms} />
			)}
		</div>
	);
}

function Tabs({ farms, selectedTab }: { farms: IFarm[]; selectedTab: number }) {
	return (
		<div className="tabs is-boxed is-small mb-0">
			<ul>
				{farms.map((farm, index) => (
					<li
						key={index}
						className={`${
							index === selectedTab ? "is-active" : ""
						}`}
					>
						<Link
							to={`${StaticRoutes.FrontPage}?${farmsViewTabURLParameter}=${index}`}
							className={`is-flex-direction-column ${
								index === selectedTab
									? "has-background-grey-lighter"
									: ""
							} custom-has-indicator custom-is-${highlightLevelToHighlightColor(
								getFarmHighlightLevel(farm)
							)}`}
						>
							<div className="is-size-6 has-text-weight-normal">
								{farm.name}
							</div>
							<div className="is-size-7 has-text-weight-normal">
								<FormattedMessage
									id={`FarmType_${farm.type}`}
								/>
							</div>
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

function Farm({ farm }: { farm: IFarm }) {
	return (
		<div className="has-background-grey-lighter px-2 py-1">
			<h1 className="title is-5 has-text-centered my-4">
				<Link
					to={DynamicRoutes.Farm.get(farm.id)}
					className="has-text-dark"
				>
					{farm.name}
					<span className="icon ml-1">
						<Icon path={mdiInformation} />
					</span>
				</Link>
			</h1>
			{farm.type === FarmType.Henhouse && <Flocks farm={farm} />}
			{farm.type === FarmType.Hatchery && <FarmSamples farm={farm} />}
		</div>
	);
}

function NoAccessRights({ canCreateNewFarms }: { canCreateNewFarms: boolean }) {
	return (
		<section className="hero is-large is-white has-text-centered">
			<div className="hero-body">
				<p className="title">
					<FormattedMessage id="Farm_List_NoItems" />
				</p>
				<p className="subtitle">
					{canCreateNewFarms ? (
						<Button className="mt-2" href={StaticRoutes.NewFarm}>
							<span className="icon">
								<Icon path={mdiPlus} />
							</span>
							<span>
								<FormattedMessage id="Farm_Button_Create" />
							</span>
						</Button>
					) : (
						<FormattedMessage id="Farm_List_NoItems_Subtitle_Other" />
					)}
				</p>
			</div>
		</section>
	);
}

function getFarmHighlightLevel(farm: IFarm): HighlightLevel {
	const flocksLevels = farm.flocks.map((flock) => getHighlightLevel(flock));
	switch (true) {
		case flocksLevels.includes(HighlightLevel.Alert):
			return HighlightLevel.Alert;
		case flocksLevels.includes(HighlightLevel.Info):
			return HighlightLevel.Info;
		default:
			return HighlightLevel.Ok;
	}
}
